import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import api from '../../config/axios';

const STATUS_OPTIONS = [
  { value: '', label: 'Hepsi' },
  { value: 'pending', label: 'Pending' },
  { value: 'retry', label: 'Retry' },
  { value: 'failed', label: 'Failed' },
  { value: 'sent', label: 'Sent' },
  { value: 'processing', label: 'Processing' }
];

function formatDate(v) {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString('tr-TR');
}

function truncate(v, n = 120) {
  const s = v == null ? '' : String(v);
  if (s.length <= n) return s;
  return s.slice(0, n) + '…';
}

export default function MessagePoolPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [filters, setFilters] = useState({
    status: 'retry',
    firm_slug: '',
    source: '',
    q: ''
  });

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);

  const offset = useMemo(() => (page - 1) * limit, [page, limit]);
  const totalPages = useMemo(() => Math.max(1, Math.ceil((total || 0) / (limit || 1))), [total, limit]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const res = await api.get('/admin/message-pool/requests', {
        params: {
          status: filters.status || undefined,
          firm_slug: filters.firm_slug || undefined,
          source: filters.source || undefined,
          q: filters.q || undefined,
          limit,
          offset
        }
      });

      const payload = res?.data?.data;
      setItems(payload?.items || []);
      setTotal(payload?.total || 0);
    } catch (e) {
      setError(e?.error || e?.message || 'Liste alınamadı');
    } finally {
      setLoading(false);
    }
  }, [filters.status, filters.firm_slug, filters.source, filters.q, limit, offset]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const runAction = async (id, action) => {
    setError('');
    setSuccess('');
    try {
      if (action === 'retry') {
        await api.post(`/admin/message-pool/requests/${id}/retry-now`, { errorMessage: 'retry_by_admin' });
        setSuccess(`ID ${id} retry kuyruğuna alındı`);
      } else if (action === 'fail') {
        await api.post(`/admin/message-pool/requests/${id}/fail-now`, { errorMessage: 'failed_by_admin' });
        setSuccess(`ID ${id} failed işaretlendi`);
      } else if (action === 'delete') {
        await api.delete(`/admin/message-pool/requests/${id}`);
        setSuccess(`ID ${id} silindi`);
      }
      await fetchList();
    } catch (e) {
      setError(e?.error || e?.message || 'İşlem başarısız');
    }
  };

  return (
    <AdminLayout>
      <div style={{ padding: '20px' }}>
        <h1 style={{ margin: '0 0 10px 0' }}>Message Pool</h1>
        <div style={{ marginBottom: '15px', color: '#666' }}>
          Pending/Retry/Failed kayıtlarını listeleyip yönetebilirsiniz.
        </div>

        {error && (
          <div style={{ marginBottom: '12px', padding: '10px 12px', border: '1px solid #f5c6cb', background: '#f8d7da', color: '#721c24', borderRadius: '8px' }}>
            {error}
          </div>
        )}
        {success && (
          <div style={{ marginBottom: '12px', padding: '10px 12px', border: '1px solid #c3e6cb', background: '#d4edda', color: '#155724', borderRadius: '8px' }}>
            {success}
          </div>
        )}

        {/* Filters */}
        <div style={{ display: 'grid', gridTemplateColumns: '220px 220px 220px 1fr 140px', gap: '10px', alignItems: 'end', marginBottom: '15px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '6px' }}>Status</label>
            <select
              value={filters.status}
              onChange={(e) => {
                setPage(1);
                setFilters((p) => ({ ...p, status: e.target.value }));
              }}
              style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '6px' }}>Firma Slug</label>
            <input
              value={filters.firm_slug}
              onChange={(e) => {
                setPage(1);
                setFilters((p) => ({ ...p, firm_slug: e.target.value }));
              }}
              placeholder="ornek-firma"
              style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '6px' }}>Source</label>
            <input
              value={filters.source}
              onChange={(e) => {
                setPage(1);
                setFilters((p) => ({ ...p, source: e.target.value }));
              }}
              placeholder="integrations:shopify"
              style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '6px' }}>Ara (phone/request_id/last_error)</label>
            <input
              value={filters.q}
              onChange={(e) => {
                setPage(1);
                setFilters((p) => ({ ...p, q: e.target.value }));
              }}
              placeholder="905.. / request_id / error"
              style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '6px' }}>Limit</label>
            <select
              value={limit}
              onChange={(e) => {
                setPage(1);
                setLimit(Number(e.target.value));
              }}
              style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }}
            >
              {[25, 50, 100, 200].map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Pagination */}
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px' }}>
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || loading}
            style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}
          >
            ←
          </button>
          <div style={{ color: '#666' }}>Sayfa {page} / {totalPages} • Toplam {total}</div>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || loading}
            style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}
          >
            →
          </button>

          <div style={{ marginLeft: 'auto', color: '#999' }}>{loading ? 'Yükleniyor…' : ''}</div>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto', border: '1px solid #eee', borderRadius: '10px', background: '#fff' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#fafafa', borderBottom: '1px solid #eee' }}>
                <th style={{ textAlign: 'left', padding: '10px' }}>ID</th>
                <th style={{ textAlign: 'left', padding: '10px' }}>Firma</th>
                <th style={{ textAlign: 'left', padding: '10px' }}>Phone</th>
                <th style={{ textAlign: 'left', padding: '10px' }}>Source</th>
                <th style={{ textAlign: 'left', padding: '10px' }}>Request ID</th>
                <th style={{ textAlign: 'left', padding: '10px' }}>Status</th>
                <th style={{ textAlign: 'left', padding: '10px' }}>Attempt</th>
                <th style={{ textAlign: 'left', padding: '10px' }}>Next</th>
                <th style={{ textAlign: 'left', padding: '10px' }}>Last Error</th>
                <th style={{ textAlign: 'left', padding: '10px' }}>Preview</th>
                <th style={{ textAlign: 'left', padding: '10px' }}>Aksiyon</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={11} style={{ padding: '14px', color: '#777' }}>Kayıt yok</td>
                </tr>
              ) : (
                items.map((r) => (
                  <tr key={r.id} style={{ borderBottom: '1px solid #f3f3f3' }}>
                    <td style={{ padding: '10px', whiteSpace: 'nowrap' }}>{r.id}</td>
                    <td style={{ padding: '10px', whiteSpace: 'nowrap' }}>{r.firm_slug}</td>
                    <td style={{ padding: '10px', whiteSpace: 'nowrap' }}>{r.phone_e164}</td>
                    <td style={{ padding: '10px', whiteSpace: 'nowrap' }}>{truncate(r.source, 40)}</td>
                    <td style={{ padding: '10px', whiteSpace: 'nowrap' }}>{truncate(r.request_id, 40)}</td>
                    <td style={{ padding: '10px', whiteSpace: 'nowrap' }}>{r.status}</td>
                    <td style={{ padding: '10px', whiteSpace: 'nowrap' }}>{r.attempt_count}</td>
                    <td style={{ padding: '10px', whiteSpace: 'nowrap' }}>{formatDate(r.next_attempt_at || r.scheduled_at)}</td>
                    <td style={{ padding: '10px' }} title={r.last_error || ''}>{truncate(r.last_error, 80)}</td>
                    <td style={{ padding: '10px' }} title={r.message_preview || ''}>{truncate(r.message_preview, 80)}</td>
                    <td style={{ padding: '10px', whiteSpace: 'nowrap' }}>
                      <button
                        onClick={() => runAction(r.id, 'retry')}
                        disabled={loading}
                        style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer', marginRight: '6px' }}
                      >
                        Retry
                      </button>
                      <button
                        onClick={() => runAction(r.id, 'fail')}
                        disabled={loading}
                        style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid #f5c6cb', background: '#fff', color: '#a71d2a', cursor: 'pointer', marginRight: '6px' }}
                      >
                        Fail
                      </button>
                      <button
                        onClick={() => runAction(r.id, 'delete')}
                        disabled={loading}
                        style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid #ddd', background: '#fff', color: '#555', cursor: 'pointer' }}
                      >
                        Sil
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
